import { Action, ActionTypes } from "./actions"
import { AudioFile, Language, Voice, AudioFormat } from "../../../../typings/types"
import { generateId } from "../../../common/idGenerator"

export interface FormField {
    value: string
    error: string | boolean
}

interface TextInputField extends FormField {
    value: string
    error: string
}
interface ButtonField extends FormField {
    value: string
    error: boolean
}
interface selectorField extends FormField {
    value: string
    error: boolean
}

export enum Fields {
    OriginalText = "originalText",
    TranslatedText = "translatedText",
    AudioFileName = "audioFileName",
    GenerateAudio = "generateAudio",
    FileFormat = "fileFormat",
    SourceVoice = "sourceVoice",
    TargetVoice = "targetVoice"
}

export enum AudioData {
    ApprovedSourceAudio = "approvedSourceAudio",
    ApprovedTargetAudio = "approvedTargetAudio"
}

export interface LanguageMap {
    [key: number]: Language
}

interface AudioFormatOptions {
    [key: number]: AudioFormat
}

type GroupedByFormFieldId<T> = {
    [key in Fields]: T
}
interface IndexableFields<T> {
    fields: GroupedByFormFieldId<T>
}
export interface ManualPageState extends IndexableFields<FormField> {
    fields: {
        [Fields.OriginalText]: TextInputField
        [Fields.TranslatedText]: TextInputField
        [Fields.AudioFileName]: TextInputField
        [Fields.GenerateAudio]: ButtonField
        [Fields.FileFormat]: selectorField
        [Fields.SourceVoice]: selectorField
        [Fields.TargetVoice]: selectorField
    }
    voiceOptions: {
        source: Voice[]
        target: Voice[]
    }
    allLanguages: LanguageMap
    languageOptions: {
        source: Language[]
        target: Language[]
    }
    audioFormatOptions: AudioFormatOptions
    sourceLanguage: number
    targetLanguage: number
    audioData: {
        [AudioData.ApprovedSourceAudio]: AudioFile | undefined
        [AudioData.ApprovedTargetAudio]: AudioFile | undefined
    }
    activeStep: number
    referenceId: string
}

export const getInitialState = (): ManualPageState => {
    return {
        fields: {
            [Fields.OriginalText]: {
                value: "",
                error: ""
            },
            [Fields.TranslatedText]: {
                value: "",
                error: ""
            },
            [Fields.AudioFileName]: {
                value: "",
                error: ""
            },
            [Fields.GenerateAudio]: {
                value: "",
                error: false
            },
            [Fields.FileFormat]: {
                value: "",
                error: false
            },
            [Fields.SourceVoice]: {
                value: "",
                error: false
            },
            [Fields.TargetVoice]: {
                value: "",
                error: false
            },
            [Fields.SourceVoice]: {
                value: "",
                error: false
            },
            [Fields.TargetVoice]: {
                value: "",
                error: false
            }
        },
        allLanguages: {},
        languageOptions: {
            source: [],
            target: []
        },
        audioFormatOptions: {},
        sourceLanguage: 0,
        targetLanguage: 0,
        voiceOptions: {
            source: [],
            target: []
        },
        audioData: {
            [AudioData.ApprovedSourceAudio]: undefined,
            [AudioData.ApprovedTargetAudio]: undefined
        },
        activeStep: 0,
        referenceId: generateId()
    }
}

export function reducer(state: ManualPageState, action: Action): ManualPageState {
    let currentFieldState: FormField

    switch (action.type) {
        case ActionTypes.UPDATE_TEXT:
            currentFieldState = state.fields[action.id]
            return {
                ...state,
                fields: {
                    ...state.fields,
                    [action.id]: {
                        ...currentFieldState,
                        value: action.value
                    }
                }
            }
        case ActionTypes.ERROR:
            currentFieldState = state.fields[action.id]
            return {
                ...state,
                fields: {
                    ...state.fields,
                    [action.id]: {
                        ...currentFieldState,
                        error: action.value
                    },
                    [Fields.GenerateAudio]: {
                        value: state.fields[Fields.GenerateAudio].value,
                        error: true
                    }
                }
            }
        case ActionTypes.CLEAR_ERROR:
            currentFieldState = state.fields[action.id]
            return {
                ...state,
                fields: {
                    ...state.fields,
                    [action.id]: {
                        ...currentFieldState,
                        error: ""
                    },
                    [Fields.GenerateAudio]: {
                        value: state.fields[Fields.GenerateAudio].value,
                        error: false
                    }
                }
            }
        case ActionTypes.UPDATE_SELECTOR:
            return {
                ...state,
                fields: {
                    ...state.fields,
                    [action.id]: {
                        ...state.fields[action.id],
                        value: action.value
                    }
                }
            }
        case ActionTypes.SET_AUDIO:
            return {
                ...state,
                audioData: {
                    ...state.audioData,
                    [action.id]: action.value
                }
            }
        case ActionTypes.UPDATE_LANGUAGES:
            const languageMap = action.value.reduce((map, lang) => {
                map[lang.id] = lang
                return map
            }, {} as LanguageMap)
            const sourceLanguageOptions = action.value
            const sourceLanguage = sourceLanguageOptions[0].id
            const targetLanguageOptions = sourceLanguageOptions.filter(
                lang => lang.id !== sourceLanguage
            )
            const targetLanguage = targetLanguageOptions[0].id

            return {
                ...state,
                allLanguages: languageMap,
                languageOptions: {
                    source: sourceLanguageOptions,
                    target: targetLanguageOptions
                },
                sourceLanguage: sourceLanguage,
                targetLanguage: targetLanguage,
                voiceOptions: {
                    ...state.voiceOptions,
                    source: languageMap[sourceLanguage].voices,
                    target: languageMap[targetLanguage].voices
                },
                fields: {
                    ...state.fields,
                    [Fields.SourceVoice]: {
                        ...state.fields[Fields.SourceVoice],
                        value: languageMap[sourceLanguage].voices[0].id.toString()
                    },
                    [Fields.TargetVoice]: {
                        ...state.fields[Fields.TargetVoice],
                        value: languageMap[targetLanguage].voices[0].id.toString()
                    }
                }
            }
        case ActionTypes.UPDATE_AUDIO_FORMATS:
            return {
                ...state,
                audioFormatOptions: action.value.reduce((map, format) => {
                    map[format.id] = format
                    return map
                }, {} as AudioFormatOptions)
            }
        case ActionTypes.UPDATE_SOURCE_LANGUAGE:
            // Update target languages to not include the newly chosen source
            const targetLanguages = Object.values(state.allLanguages).filter(
                lang => lang.id.toString() !== action.value.toString()
            )
            return {
                ...state,
                sourceLanguage: action.value,
                targetLanguage: targetLanguages[0].id,
                languageOptions: {
                    ...state.languageOptions,
                    target: targetLanguages
                },
                voiceOptions: {
                    ...state.voiceOptions,
                    source: state.allLanguages[action.value].voices,
                    target: targetLanguages[0].voices
                },
                fields: {
                    ...state.fields,
                    [Fields.SourceVoice]: {
                        ...state.fields[Fields.SourceVoice],
                        value: state.allLanguages[action.value].voices[0].id.toString()
                    }
                }
            }
        case ActionTypes.UPDATE_TARGET_LANGUAGE:
            return {
                ...state,
                targetLanguage: action.value,
                voiceOptions: {
                    ...state.voiceOptions,
                    target: state.allLanguages[action.value].voices
                },
                fields: {
                    ...state.fields,
                    [Fields.TargetVoice]: {
                        ...state.fields[Fields.TargetVoice],
                        value: state.allLanguages[action.value].voices[0].id.toString()
                    }
                }
            }
        case ActionTypes.ADVANCE_STEPPER:
            return {
                ...state,
                activeStep: state.activeStep + 1
            }

        case ActionTypes.REVERSE_STEPPER:
            return {
                ...state,
                activeStep: state.activeStep - 1
            }
        case ActionTypes.RESET:
            return getInitialState()
        default:
            return state
    }
}
